import React from 'react';
import { RelBox } from '../../../api/ai.api.types';
import { visibleViewport } from '../helper/frameCapture';

interface DetectionOverlayProps {
    meterBox: RelBox | null;
    displayBox?: RelBox | null;
    displayValue?: string;
    // True while the shown value is still waiting for consensus (seen on one image only) — it
    // renders dimmed until a second image confirms it.
    displayValuePending?: boolean;
    // Label shown under the display box while the value is pending ("wird geprüft…"), so a
    // recognized-but-not-yet-adopted value doesn't read as "why don't you take it?". Passed in as
    // plain text — this overlay is aria-hidden and deliberately i18n-free.
    pendingLabel?: string;
    // Photo snippet of the display (data URL). When set it replaces the displayValue text inside
    // the display box — used in the confirm step so the user compares the actual photo the AI
    // read from against the editable value below.
    displayImage?: string;
    meterLabel?: string;
    mode: 'tracking' | 'snap';
    // Shared score-ramp color (white→green while aligning, green afterwards); meter box, display
    // box and the recognized value all follow it so "everything green" reads as the goal state.
    accentColor?: string;
    // True once alignment has snapped (see alignmentGuidance.computeSnap). The accent color ramp
    // is now continuous, so the "now it reads" moment needs its own unmistakable marker.
    snapped?: boolean;
}

// Overlay layer above the full-screen video. All captures sent to the AI (and the tracker) are
// cropped to the visible object-fit:cover viewport (see frameCapture.coverCropRegion), so every
// box arriving here is already relative (0..1) to this overlay — plain percentage positioning,
// no coordinate mapping. Snap mode (Approach A) uses a longer CSS transition so the 1/s server
// boxes ease in smoothly; tracking mode (Approach B) uses a short one so the box "sticks".
const DEFAULT_ACCENT = 'rgb(74, 222, 128)'; // tailwind green-400

// Confirm-step zoom: with a photo snippet to verify, the display box flies from its detected
// position to a large centered view (80% of the screen width, aspect preserved) so the digits
// are actually readable next to the editable value below.
const CONFIRM_ZOOM_WIDTH = 0.9;
const CONFIRM_ZOOM_MAX_HEIGHT = 0.35;
const CONFIRM_ZOOM_TOP = 0.2;
// Springy bezier with slight overshoot — same feel as the alignment brackets.
const ZOOM_TRANSITION = 'all 0.45s cubic-bezier(0.3, 1.3, 0.4, 1)';

const zoomedDisplayBox = (box: RelBox): RelBox => {
    const viewport = typeof window !== 'undefined' ? visibleViewport() : { width: 0, height: 0 };
    if (!viewport.width || !viewport.height || box.w <= 0 || box.h <= 0) {
        return { x: (1 - CONFIRM_ZOOM_WIDTH) / 2, y: CONFIRM_ZOOM_TOP, w: CONFIRM_ZOOM_WIDTH, h: 0.25 };
    }
    // Preserve the display's on-screen pixel aspect ratio at the zoomed size. A display is
    // physically always wider than tall — a portrait box means the meter was photographed
    // rotated, and cropImage rotates the snippet back to landscape, so the container follows.
    let pixelAspect = (box.w * viewport.width) / (box.h * viewport.height);
    if (pixelAspect < 1) {
        pixelAspect = 1 / pixelAspect;
    }
    let w = CONFIRM_ZOOM_WIDTH;
    let h = (w * viewport.width) / (pixelAspect * viewport.height);
    if (h > CONFIRM_ZOOM_MAX_HEIGHT) {
        w *= CONFIRM_ZOOM_MAX_HEIGHT / h;
        h = CONFIRM_ZOOM_MAX_HEIGHT;
    }
    return { x: (1 - w) / 2, y: CONFIRM_ZOOM_TOP, w, h };
};

const DetectionOverlay = ({
    meterBox,
    displayBox,
    displayValue,
    displayValuePending = false,
    pendingLabel,
    displayImage,
    meterLabel,
    mode,
    accentColor = DEFAULT_ACCENT,
    snapped = false,
}: DetectionOverlayProps) => {
    const boxStyle = (box: RelBox): React.CSSProperties => ({
        left: `${box.x * 100}%`,
        top: `${box.y * 100}%`,
        width: `${box.w * 100}%`,
        height: `${box.h * 100}%`,
        transition: (mode === 'tracking' ? 'all 0.08s linear' : 'all 0.3s ease-out') + ', border-color 0.2s linear',
    });

    return (
        <div className={'absolute inset-0 overflow-hidden pointer-events-none'} aria-hidden={true}>
            {meterBox && (
                <div
                    data-testid="detection-box"
                    className={'absolute border-2 rounded-md'}
                    style={{
                        ...boxStyle(meterBox),
                        borderColor: accentColor,
                        opacity: mode === 'tracking' ? 1 : 0.85,
                        // Snap kick: the ramp is continuous, so the "now it reads" moment needs its own
                        // unmistakable marker — thicker border + glow. box-shadow is fine here (closed
                        // rectangle, no L-shape clipping).
                        borderWidth: snapped ? '3px' : undefined,
                        boxShadow: snapped ? `0 0 14px ${accentColor}` : undefined,
                        transition: (mode === 'tracking' ? 'all 0.08s linear' : 'all 0.3s ease-out') + ', border-color 0.2s linear, box-shadow 0.2s linear',
                    }}
                >
                    {meterLabel && (
                        <span className={'absolute -top-6 left-0 text-xs text-white bg-black bg-opacity-60 rounded px-1 py-0.5 whitespace-nowrap'}>
                            {meterLabel}
                        </span>
                    )}
                </div>
            )}
            {displayBox && (
                <div
                    data-testid="display-box"
                    className={
                        'absolute flex items-center justify-center overflow-hidden ' +
                        (displayImage ? 'border-2 rounded-lg bg-black bg-opacity-70' : 'border rounded-sm bg-black bg-opacity-40')
                    }
                    style={{
                        ...boxStyle(displayImage ? zoomedDisplayBox(displayBox) : displayBox),
                        borderColor: accentColor,
                        ...(displayImage && { transition: ZOOM_TRANSITION, boxShadow: '0 6px 28px rgba(0, 0, 0, 0.55)' }),
                    }}
                >
                    {displayImage ? (
                        <img
                            data-testid="display-crop"
                            src={displayImage}
                            alt=""
                            className={'w-full h-full'}
                            style={{ objectFit: 'contain' }}
                        />
                    ) : (
                        displayValue && (
                            <span
                                data-testid="display-value"
                                data-pending={displayValuePending ? 'true' : 'false'}
                                className={'font-mono font-bold leading-none px-1 whitespace-nowrap'}
                                style={{
                                    fontSize: 'min(4vw, 1.25rem)',
                                    textShadow: '0 0 4px rgba(0,0,0,0.9)',
                                    color: accentColor,
                                    opacity: displayValuePending ? 0.6 : 1,
                                    transition: 'color 0.2s linear, opacity 0.2s linear',
                                }}
                            >
                                {displayValue}
                            </span>
                        )
                    )}
                </div>
            )}
            {/* Sibling of the display box (which clips via overflow-hidden): pending marker below
                the box, so the user knows a shown-but-dimmed value is being verified, not stuck. */}
            {displayBox && !displayImage && displayValue && displayValuePending && pendingLabel && (
                <div
                    className={'absolute flex justify-center'}
                    style={{ left: `${displayBox.x * 100}%`, top: `${(displayBox.y + displayBox.h) * 100}%`, width: `${displayBox.w * 100}%` }}
                >
                    <span
                        data-testid="display-pending-label"
                        className={'mt-1 text-xs text-white bg-black bg-opacity-60 rounded px-1.5 py-0.5 whitespace-nowrap'}
                    >
                        {pendingLabel}
                    </span>
                </div>
            )}
        </div>
    );
};

export default DetectionOverlay;
